<template>
  <v-dialog v-model="internalValue" width="560" persistent>
    <v-card>
      <v-card-title class="d-flex align-center pa-4">
        <span class="subtitle-1 font-weight-bold">Faixas de crescimento</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="cancelar"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-4 pt-0">
        <p class="caption grey--text mb-3" style="line-height: 1.4">
          O crescimento aplicado a cada colaborador depende da própria média dele. A faixa de menor
          "de" precisa começar no piso ({{ formatarPiso }}). O "até" é só um rótulo de referência —
          quem decide onde cada faixa realmente termina é o "de" da faixa seguinte (ver "Como vai
          valer" abaixo).
        </p>

        <div class="d-flex caption font-weight-bold grey--text text--darken-1 mb-1" style="gap: 8px">
          <div style="flex: 1">De</div>
          <div style="flex: 1">Até (rótulo)</div>
          <div style="flex: 1">Crescimento</div>
          <div style="width: 32px"></div>
        </div>

        <div
          v-for="(faixa, idx) in faixasLocal"
          :key="idx"
          class="d-flex align-start mb-2"
          style="gap: 8px"
        >
          <v-text-field
            :value="faixa.de"
            @input="setDe(idx, $event)"
            @blur="deTocado[idx] = true"
            type="number"
            dense
            outlined
            :disabled="idx === 0"
            :error-messages="deTocado[idx] ? erroDe(faixa) : ''"
            :hide-details="!(deTocado[idx] && erroDe(faixa))"
            style="flex: 1"
          ></v-text-field>
          <v-text-field
            :value="faixa.ate"
            @input="setAte(idx, $event)"
            @blur="ateTocado[idx] = true"
            type="number"
            dense
            outlined
            placeholder="opcional"
            :error-messages="ateTocado[idx] ? erroAte(faixa) : ''"
            :hide-details="!(ateTocado[idx] && erroAte(faixa))"
            style="flex: 1"
          ></v-text-field>
          <v-text-field
            :value="faixa.crescimento"
            @input="setCrescimento(idx, $event)"
            suffix="%"
            type="number"
            dense
            outlined
            hide-details
            style="flex: 1"
          ></v-text-field>
          <v-btn
            icon
            small
            :disabled="!podeRemover"
            @click="removerEssaFaixa(idx)"
            style="margin-top: 2px"
          >
            <v-icon small>mdi-delete-outline</v-icon>
          </v-btn>
        </div>

        <v-btn
          text
          small
          color="primary"
          :disabled="!podeAdicionar"
          @click="adicionarFaixaNova"
          class="mt-1"
        >
          <v-icon small class="mr-1">mdi-plus</v-icon>Adicionar faixa
        </v-btn>

        <v-alert v-if="pendencias.length" type="warning" dense text class="caption mt-3 mb-0">
          Falta: {{ pendencias.join(", ") }}.
        </v-alert>

        <!-- Indicacao visual pedida pelo negocio em 2026-09-10: o que foi
             digitado (rotulo) pode nao bater com o que o sistema realmente
             aplica (o "de" da proxima faixa) — mostrar os dois evita erro de
             cadastro. -->
        <div v-if="!pendencias.length" class="mt-3 pa-2" style="background: rgba(25,118,210,0.05); border-radius: 6px">
          <p class="caption font-weight-bold ma-0 mb-1" style="color: #1976d2">Como vai valer</p>
          <p v-for="(faixa, idx) in faixasEfetivas" :key="idx" class="caption ma-0" style="line-height: 1.5">
            {{ formatarIntervaloEfetivo(faixa) }} → <strong>{{ faixa.crescimento }}%</strong>
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-spacer></v-spacer>
        <v-btn text @click="cancelar">Cancelar</v-btn>
        <v-btn color="primary" :disabled="pendencias.length > 0" @click="salvar">Salvar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  criarFaixaInicial,
  adicionarFaixa,
  podeAdicionarFaixa,
  podeRemoverFaixa,
  removerFaixa,
  faixaDeErro,
  faixaAteErro,
  calcularFaixasEfetivas,
  pendenciasFaixasCrescimento
} from "./faixasCrescimentoUtils";

// Copia local editada com Cancelar/Salvar explicitos, em vez de editar
// form.faixasCrescimento direto — abrir o modal e fechar sem salvar nao pode
// deixar rastro no form do pai.
export default {
  name: "ModalFaixasCrescimento",

  props: {
    value: { type: Boolean, default: false },
    faixas: { type: Array, default: () => [] },
    piso: { type: [Number, String], default: 0 }
  },

  data() {
    return {
      faixasLocal: [],
      // "campo tocado" por indice, mesmo padrao de SeletorOrigemMeta.pisoTocado.
      deTocado: {},
      ateTocado: {}
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        if (!val) this.cancelar();
      }
    },

    formatarPiso() {
      const n = parseFloat(this.piso);
      return isNaN(n) ? "—" : n.toLocaleString("pt-BR");
    },

    podeAdicionar() {
      return podeAdicionarFaixa(this.faixasLocal);
    },

    podeRemover() {
      return podeRemoverFaixa(this.faixasLocal);
    },

    pendencias() {
      return pendenciasFaixasCrescimento(this.faixasLocal, this.piso);
    },

    faixasEfetivas() {
      return calcularFaixasEfetivas(this.faixasLocal);
    }
  },

  watch: {
    // Reabastece a copia local toda vez que o modal abre — ve sempre o
    // ultimo valor salvo, nunca um estado de uma edicao cancelada antes.
    value(aberto) {
      if (aberto) this.iniciar();
    }
  },

  created() {
    if (this.value) this.iniciar();
  },

  methods: {
    iniciar() {
      this.faixasLocal = this.faixas && this.faixas.length
        ? this.faixas.map(f => ({ ...f }))
        : [criarFaixaInicial(this.piso)];
      this.deTocado = {};
      this.ateTocado = {};
    },

    erroDe(faixa) {
      return faixaDeErro(faixa);
    },

    erroAte(faixa) {
      return faixaAteErro(faixa);
    },

    formatarIntervaloEfetivo(faixa) {
      const semTeto = faixa.ateEfetivo == null;
      return semTeto
        ? `A partir de ${faixa.deEfetivo}`
        : `De ${faixa.deEfetivo} até menos de ${faixa.ateEfetivo}`;
    },

    setDe(idx, valor) {
      this.$set(this.faixasLocal[idx], "de", valor);
    },

    setAte(idx, valor) {
      this.$set(this.faixasLocal[idx], "ate", valor);
    },

    setCrescimento(idx, valor) {
      this.$set(this.faixasLocal[idx], "crescimento", valor);
    },

    adicionarFaixaNova() {
      if (!this.podeAdicionar) return;
      this.faixasLocal = adicionarFaixa(this.faixasLocal);
    },

    removerEssaFaixa(idx) {
      if (!this.podeRemover) return;
      this.faixasLocal = removerFaixa(this.faixasLocal, idx);
    },

    cancelar() {
      this.$emit("input", false);
    },

    salvar() {
      if (this.pendencias.length) return;
      this.$emit("save", this.faixasLocal.map(f => ({ ...f })));
      this.$emit("input", false);
    }
  }
};
</script>
